import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PRODUCT_TYPES,
  ORDER_STATUS,
  ORDER_STATUS_LABELS,
  PAYMENT_TYPE_LABELS,
  JALUZI_SISTEM_TIPLERI,
  DIRECTION_TYPES,
  DIKEY_DIRECTION_TYPES,
  MOTOR_TYPES,
  TUL_PILE_MODELS,
  KANAT_PILE_MODELS
} from '../constants';
import { ROLES } from '../hooks/useAuth';

const translateMap = (sourceMap, keyPrefix, t) => {
  return Object.fromEntries(
    Object.entries(sourceMap).map(([key, fallback]) => [
      key,
      t(`${keyPrefix}.${key}`, { defaultValue: fallback })
    ])
  );
};

const translateProductTypes = (t) => {
  return Object.fromEntries(
    Object.entries(PRODUCT_TYPES).map(([key, value]) => [
      key,
      {
        ...value,
        label: t(`constants.productTypes.${key}`, { defaultValue: value.label })
      }
    ])
  );
};

export const useLocalizedConstants = () => {
  const { t, i18n } = useTranslation();

  return useMemo(() => ({
    PRODUCT_TYPES: translateProductTypes(t),
    ORDER_STATUS,
    ORDER_STATUS_LABELS: translateMap(ORDER_STATUS_LABELS, 'constants.orderStatus', t),
    PAYMENT_TYPE_LABELS: translateMap(PAYMENT_TYPE_LABELS, 'constants.paymentTypes', t),
    JALUZI_SISTEM_TIPLERI: translateMap(JALUZI_SISTEM_TIPLERI, 'constants.jaluziSistemTipleri', t),
    DIRECTION_TYPES: translateMap(DIRECTION_TYPES, 'constants.directionTypes', t),
    DIKEY_DIRECTION_TYPES: translateMap(DIKEY_DIRECTION_TYPES, 'constants.dikeyDirectionTypes', t),
    MOTOR_TYPES: translateMap(MOTOR_TYPES, 'constants.motorTypes', t),
    TUL_PILE_MODELS: translateMap(TUL_PILE_MODELS, 'constants.pileModels', t),
    KANAT_PILE_MODELS: translateMap(KANAT_PILE_MODELS, 'constants.pileModels', t),
    ROLE_LABELS: translateMap(
      {
        [ROLES.ADMIN]: 'Admin',
        [ROLES.SATISCI]: 'Satışcı',
        [ROLES.MUHASEBECI]: 'Muhasebeci',
        [ROLES.MONTAJCI]: 'Montajcı',
        [ROLES.YAZILIMCI]: 'Yazılımcı'
      },
      'constants.roles',
      t
    ),
    getRoleName: (role) => t(`constants.roles.${role}`, {
      defaultValue: {
        [ROLES.ADMIN]: 'Admin',
        [ROLES.SATISCI]: 'Satışcı',
        [ROLES.MUHASEBECI]: 'Muhasebeci',
        [ROLES.MONTAJCI]: 'Montajcı',
        [ROLES.YAZILIMCI]: 'Yazılımcı'
      }[role] || role
    }),
    getProductTypeLabel: (type) => t(`constants.productTypes.${type}`, {
      defaultValue: PRODUCT_TYPES[type]?.label || type
    }),
    getOrderStatusLabel: (status) => t(`constants.orderStatus.${status}`, {
      defaultValue: ORDER_STATUS_LABELS[status] || status
    }),
    getPaymentTypeLabel: (type) => t(`constants.paymentTypes.${type}`, {
      defaultValue: PAYMENT_TYPE_LABELS[type] || type
    }),
    getPileModelLabel: (rawValue) => {
      if (!rawValue) return rawValue;
      const entry = Object.entries(TUL_PILE_MODELS).find(([, val]) => val === rawValue);
      if (entry) return t(`constants.pileModels.${entry[0]}`, { defaultValue: rawValue });
      return rawValue;
    },
    language: i18n.language
  }), [t, i18n.language]);
};
